import {Component, inject, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, effect} from '@angular/core';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {FormBuilder, FormGroup, ReactiveFormsModule, FormControl} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';
import {DatevIntegrationRestService} from "../services/datev-integration.rest.service";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ResponseModel} from "../../../core/base/response.model";
import {DatevIntegrationService} from "../services/datev-integration.service";
import {DialogModule} from "../../../shared/dialog/dialog.module";
import {DialogTextModel} from "../../../core/cms/models/dialog.text.model";
import {MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {DialogComponent} from "../../../shared/dialog/dialog.component";
import {UserService} from "../../../core/user/services/user.service";
import {ProtocolDialogComponent} from "../protocol-dialog/protocol-dialog.component";

@Component({
  selector: 'app-set-config-form',
  imports: [
    MatSlideToggleModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    DialogModule,
  ],
  templateUrl: './set-config-form.component.html',
  styleUrl: './set-config-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetConfigFormComponent {
  configForm: FormGroup;
  docTypes: any[] = [];
  datevDocuments: any[] = [];
  private formBuilder = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);

  protected readonly datevRestService = inject(DatevIntegrationRestService);
  protected readonly datevService = inject(DatevIntegrationService);
  protected readonly userService = inject(UserService);
  loading: boolean = true;
  private isFormInitialized = false;

  constructor() {
    this.configForm = this.formBuilder.group({
      client_id: [''],
      note: [''],
      notify: [false],
      documents: this.formBuilder.group({}),
      send: this.formBuilder.group({})
    });

    this.datevRestService.getDocTypes()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (docType: any) => {
          this.docTypes = Array.isArray(docType) ? docType : [docType];
          const docGroup = this.configForm.get('documents') as FormGroup;
          const sendGroup = this.configForm.get('send') as FormGroup;
          this.docTypes.forEach(doc => {
            docGroup.addControl(doc.attribute, new FormControl(''));
            sendGroup.addControl(doc.attribute + '-send', new FormControl(false));
          });

          this.loading = false;
          this.isFormInitialized = true;
          const idf = this.userService.selectedIdf();
          if (idf) {
            this.loadIdfData(idf);
          }
          this.cdr.markForCheck();
        },
        error: (err: any) => console.error('Error loading docTypes:', err)
      });

    effect(() => {
      const idf = this.userService.selectedIdf();
      if (idf && this.isFormInitialized) {
        this.loadIdfData(idf);
      }
    });
  }

  private loadIdfData(idf: string) {
    this.datevRestService.getDatevDocuments(idf)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ResponseModel) => {
          this.datevDocuments = response.return_object;
          this.cdr.markForCheck();
        },
        error: (err: any) => console.error('Error loading DATEV Documents:', err)
      });

    this.datevRestService.getCustomerCongifuration(idf)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ResponseModel) => {
          this.configForm.patchValue(response.return_object);
          console.log(response.return_object)
          const berater = response.return_object.client_id.split('-');
          this.datevService.setBerater(berater[0])
          this.cdr.markForCheck();
        },
        error: (err: any) => console.error('Error getting configuration', err)
      });
  }

  submitConfigForm() {
    const data = this.configForm.value;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;

    data['idf'] = this.userService.selectedIdf();
    this.datevRestService.saveConfiguration(data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ResponseModel) => {
          dialogConfig.data = new DialogTextModel({
            title: 'Konfiguration wird gespeichert',
            type: 'close'
          });
          const submitDialogRef = this.dialog.open(DialogComponent, dialogConfig)
          setTimeout(() => {
            submitDialogRef.close();
          }, 3000);
          this.cdr.markForCheck();
        },
        error: (err: any) => console.error('Error saving configuration', err)
      });
  }

  onDeactivate() {
    const confirmConfig = new MatDialogConfig();
    confirmConfig.autoFocus = true;
    confirmConfig.data = new DialogTextModel({
      title: 'Möchten Sie wirklich deaktivieren?',
      type: 'confirm'
    });
    const confirmDialogRef = this.dialog.open(DialogComponent, confirmConfig);
    confirmDialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: any) => {
        if (result) {
          const dialogConfig = new MatDialogConfig();
          dialogConfig.autoFocus = true;
          this.datevRestService.deactivate(this.userService.selectedIdf())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (response: ResponseModel) => {
                dialogConfig.data = new DialogTextModel({
                  title: 'Deaktiviert',
                  type: 'close'
                });
                const dialogRef = this.dialog.open(DialogComponent, dialogConfig)
                setTimeout(() => {
                  dialogRef.close();
                }, 3000);
                this.datevService.setActivated(false);
                this.cdr.markForCheck();
              },
              error: (err: any) => console.error('Error deactivating Datev:', err)
            });
        }
      });
  }

  protected openProtocolDialog() {
    this.datevRestService.getDatevLogs(this.userService.selectedIdf())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          const logs = result.return_object;

          this.dialog.open(ProtocolDialogComponent, {
            width: '700px',
            data: { logs: logs, types: this.docTypes },
          });
        },
        error: (err: any) => console.error('Error loading logs:', err)
      });
  }

  protected readonly onpagehide = onpagehide;

  protected createNewToken() {
    this.datevService.setActivated(false);
    this.datevService.setHasMoreClients(true);
    // this.configForm.patchValue({})
  }
}
